/**
 * Drag-and-drop target.
 *
 * Wraps the whole gallery rather than a small box: dropping photos "onto the
 * album" is the natural gesture, and hunting for a target rectangle is not.
 * Folders are walked recursively via the FileSystem entries API, so dragging a
 * `Barcelona 2026/` directory in works the way a desktop user expects.
 */

import { useCallback, useRef, useState } from "react";
import type { ReactNode } from "react";
import "./UploadDropzone.css";

export interface UploadDropzoneProps {
  children: ReactNode;
  onFiles: (files: File[]) => void;
  disabled?: boolean;
  className?: string;
}

export function UploadDropzone({
  children,
  onFiles,
  disabled,
  className,
}: UploadDropzoneProps) {
  const [dragging, setDragging] = useState(false);
  // dragenter/dragleave fire for every child element; counting keeps the
  // overlay from flickering as the pointer crosses tile boundaries.
  const depth = useRef(0);

  const onDragEnter = useCallback(
    (event: React.DragEvent) => {
      if (disabled) return;
      // Ignore drags that carry no files, e.g. selected text.
      if (!event.dataTransfer.types.includes("Files")) return;
      event.preventDefault();
      depth.current += 1;
      setDragging(true);
    },
    [disabled],
  );

  const onDragLeave = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    depth.current = Math.max(0, depth.current - 1);
    if (depth.current === 0) setDragging(false);
  }, []);

  const onDragOver = useCallback(
    (event: React.DragEvent) => {
      if (disabled) return;
      if (!event.dataTransfer.types.includes("Files")) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = "copy";
    },
    [disabled],
  );

  const onDrop = useCallback(
    async (event: React.DragEvent) => {
      if (disabled) return;
      event.preventDefault();
      depth.current = 0;
      setDragging(false);

      const files = await collectFiles(event.dataTransfer);
      if (files.length > 0) onFiles(files);
    },
    [disabled, onFiles],
  );

  return (
    <div
      className={className}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDragOver={onDragOver}
      onDrop={(event) => void onDrop(event)}
    >
      {children}

      {dragging && (
        <div className="fc-dropzone" aria-hidden="true">
          <div className="fc-dropzone__panel">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 16V4M12 4L7 9M12 4l5 5" />
              <path d="M4 17v1a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3v-1" />
            </svg>
            <p className="fc-dropzone__title">Drop your photos here</p>
            <p className="fc-dropzone__hint">Folders keep their structure</p>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Flatten a drop into a list of files, descending into any directories.
 *
 * `webkitGetAsEntry` is the only way to see directory contents from a drop; it
 * is prefixed but supported everywhere we target. Entries must be read
 * synchronously during the drop event, hence collecting them first.
 */
async function collectFiles(transfer: DataTransfer): Promise<File[]> {
  const entries: FileSystemEntry[] = [];

  for (const item of transfer.items) {
    if (item.kind !== "file") continue;
    const entry = item.webkitGetAsEntry?.();
    if (entry) entries.push(entry);
  }

  if (entries.length === 0) return [...transfer.files];

  const files: File[] = [];
  const MAX_FILES = 5000;

  async function walk(entry: FileSystemEntry, depth = 0): Promise<void> {
    if (files.length >= MAX_FILES || depth > 8) return;

    if (entry.isFile) {
      const file = await new Promise<File | null>((resolve) => {
        (entry as FileSystemFileEntry).file(resolve, () => resolve(null));
      });
      // Skip the invisible metadata files macOS and Windows scatter around.
      if (file && !file.name.startsWith(".") && file.name !== "Thumbs.db")
        files.push(file);
      return;
    }

    if (entry.isDirectory) {
      const reader = (entry as FileSystemDirectoryEntry).createReader();
      // readEntries returns at most 100 at a time and must be called until it
      // returns an empty batch — a classic source of "only 100 photos uploaded".
      for (;;) {
        const batch = await new Promise<FileSystemEntry[]>((resolve) => {
          reader.readEntries(resolve, () => resolve([]));
        });
        if (batch.length === 0) break;
        for (const child of batch) await walk(child, depth + 1);
      }
    }
  }

  await Promise.all(entries.map((entry) => walk(entry)));
  return files;
}
